import { http } from '@/api/http';
import { isAdminLocalModeEnabled, isPublicMockEnabled } from '@/config/featureFlags';
import type { DisplayOrderUpdateInput } from '@/features/board/adminOrdering';
import { withBoardReadFallback } from '@/features/board/requestFallback';
import {
  createMockCatalogItem,
  deleteMockCatalogItem,
  fetchMockAdminCatalogItem,
  fetchMockAdminCatalogItems,
  fetchMockCatalogItem,
  fetchMockCatalogItems,
  updateMockCatalogItemDisplayOrders,
  updateMockCatalogItem,
} from '@/features/content/mockData';
import type {
  CatalogCategoryMutationInput,
  CatalogCategoryResponse,
  AdminCatalogItemDetail,
  AdminCatalogItemSummary,
  CatalogItemDetail,
  CatalogItemMutationInput,
  CatalogItemSummary,
} from '@/features/content/types';
import type { CatalogCategorySlug } from '@/routes/routeRegistry';

const appendIfPresent = (formData: FormData, key: string, value: File | null) => {
  if (value) {
    formData.append(key, value);
  }
};

const createCatalogItemFormData = (input: CatalogItemMutationInput) => {
  const formData = new FormData();
  formData.append('category', input.category);
  formData.append('title', input.title);
  formData.append('summary', input.summary);
  formData.append('detailContentJson', JSON.stringify(input.detailContent));
  if (input.translations?.length) {
    formData.append('translationsJson', JSON.stringify(input.translations));
  }
  formData.append('isPublished', String(input.isPublished));
  formData.append('publishedAt', input.publishedAt);
  formData.append('removePreviewImages', String(input.removePreviewImages));
  formData.append('mainPreviewImageIndex', String(input.mainPreviewImageIndex));
  input.previewImages.forEach((file) => {
    formData.append('previewImages', file);
  });
  formData.append('previewImageSlotsJson', JSON.stringify(input.previewImageSlots));
  appendIfPresent(formData, 'detailImage', input.detailImage);
  appendIfPresent(formData, 'detailSectionImage', input.detailSectionImage);
  appendIfPresent(formData, 'comparisonGeneralImage', input.comparisonGeneralImage);
  appendIfPresent(formData, 'comparisonPremiumImage', input.comparisonPremiumImage);
  appendIfPresent(formData, 'timelineImage', input.timelineImage);
  appendIfPresent(formData, 'valueBeforeImage', input.valueBeforeImage);
  appendIfPresent(formData, 'valueAfterImage', input.valueAfterImage);
  appendIfPresent(formData, 'additionalDetailImage', input.additionalDetailImage);
  return formData;
};

const shouldUsePublicContentMocks = isPublicMockEnabled();
const shouldUseAdminContentLocalMode = isAdminLocalModeEnabled();

const defaultCatalogCategoryResponses: CatalogCategoryResponse[] = [
  {
    id: 1,
    slug: 'co2-natural-refrigerant',
    label: 'CO₂ 자연냉매',
    displayOrder: 0,
    isVisible: true,
    itemCount: 1,
  },
  {
    id: 2,
    slug: 'refrigeration-system',
    label: '냉동·냉장 시스템',
    displayOrder: 1,
    isVisible: true,
    itemCount: 0,
  },
  {
    id: 3,
    slug: 'energy-solution',
    label: '에너지 솔루션',
    displayOrder: 2,
    isVisible: true,
    itemCount: 0,
  },
  {
    id: 4,
    slug: 'mechanical-hvac',
    label: '공조 기계설비',
    displayOrder: 3,
    isVisible: true,
    itemCount: 0,
  },
];

export const fetchCatalogCategories = async (): Promise<CatalogCategoryResponse[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get('/api/v1/catalog-categories'),
    fallbackRequest: () => Promise.resolve(defaultCatalogCategoryResponses),
    label: 'catalog categories',
    useMock: shouldUsePublicContentMocks,
  });
};

export const fetchAdminCatalogCategories = async (): Promise<CatalogCategoryResponse[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get('/api/v1/admin/catalog-categories'),
    fallbackRequest: () => Promise.resolve(defaultCatalogCategoryResponses),
    label: 'admin catalog categories',
    useMock: shouldUseAdminContentLocalMode,
  });
};

export const createCatalogCategory = async (
  input: CatalogCategoryMutationInput,
): Promise<CatalogCategoryResponse> => {
  return http.post('/api/v1/admin/catalog-categories', input);
};

export const updateCatalogCategory = async (
  categoryId: number,
  input: CatalogCategoryMutationInput,
): Promise<CatalogCategoryResponse> => {
  return http.put(`/api/v1/admin/catalog-categories/${String(categoryId)}`, input);
};

export const updateCatalogCategoryDisplayOrders = async (
  items: DisplayOrderUpdateInput[],
): Promise<void> => {
  return http.patch('/api/v1/admin/catalog-categories/display-orders', { items });
};

export const fetchCatalogItemBySlug = async (slug: string): Promise<CatalogItemDetail> => {
  return withBoardReadFallback({
    apiRequest: () => http.get(`/api/v1/catalog-items/${slug}`),
    fallbackRequest: async () => {
      const categories = await fetchCatalogCategories();
      const fallbackItem = await Promise.any(
        categories.map((category) => fetchMockCatalogItem(category.slug, slug)),
      );

      return fallbackItem;
    },
    label: `catalog item:${slug}`,
    useMock: shouldUsePublicContentMocks,
  });
};

export const fetchCatalogItems = async (
  category: CatalogCategorySlug,
): Promise<CatalogItemSummary[]> => {
  return withBoardReadFallback({
    apiRequest: () =>
      http.get('/api/v1/catalog-items', {
        params: {
          category,
        },
      }),
    fallbackRequest: () => fetchMockCatalogItems(category),
    label: `catalog items:${category}`,
    useMock: shouldUsePublicContentMocks,
  });
};

export const fetchAdminCatalogItems = async (
  category?: CatalogCategorySlug,
): Promise<AdminCatalogItemSummary[]> => {
  return withBoardReadFallback({
    apiRequest: () =>
      http.get('/api/v1/admin/catalog-items', {
        params: category
          ? {
              category,
            }
          : undefined,
      }),
    fallbackRequest: () => fetchMockAdminCatalogItems(category),
    label: `admin catalog items:${category ?? 'all'}`,
    useMock: shouldUseAdminContentLocalMode,
  });
};

export const fetchAdminCatalogItem = async (
  catalogItemId: number,
): Promise<AdminCatalogItemDetail> => {
  return withBoardReadFallback({
    apiRequest: () => http.get(`/api/v1/admin/catalog-items/${String(catalogItemId)}`),
    fallbackRequest: () => fetchMockAdminCatalogItem(catalogItemId),
    label: `admin catalog item:${String(catalogItemId)}`,
    useMock: shouldUseAdminContentLocalMode,
  });
};

export const createCatalogItem = async (
  input: CatalogItemMutationInput,
): Promise<AdminCatalogItemDetail> => {
  if (shouldUseAdminContentLocalMode) {
    return createMockCatalogItem(input);
  }

  return http.post('/api/v1/admin/catalog-items', createCatalogItemFormData(input));
};

export const updateCatalogItem = async (
  catalogItemId: number,
  input: CatalogItemMutationInput,
): Promise<AdminCatalogItemDetail> => {
  if (shouldUseAdminContentLocalMode) {
    return updateMockCatalogItem(catalogItemId, input);
  }

  return http.put(
    `/api/v1/admin/catalog-items/${String(catalogItemId)}`,
    createCatalogItemFormData(input),
  );
};

export const deleteCatalogItem = async (catalogItemId: number): Promise<void> => {
  if (shouldUseAdminContentLocalMode) {
    return deleteMockCatalogItem(catalogItemId);
  }

  return http.delete(`/api/v1/admin/catalog-items/${String(catalogItemId)}`);
};

export const updateCatalogItemDisplayOrders = async (
  items: DisplayOrderUpdateInput[],
): Promise<void> => {
  if (shouldUseAdminContentLocalMode) {
    return updateMockCatalogItemDisplayOrders(items);
  }

  return http.patch('/api/v1/admin/catalog-items/display-orders', { items });
};
